import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import Title from "../../component/title/Title";
import Text from "../../component/text/Text";
import SearchInput from "../../component/searchInput/SearchInput";
import { SecondaryButton } from "../../component/buttonComponent/ButtonComponent";
import ModalCloseButton from "../../component/modal/ModalCloseButton";
import "../../component/modal/DeleteModal.css";
import "./CampaignAudienceStudentsModal.css";

const modalBoxStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(40rem, calc(100vw - 1.5rem))",
  bgcolor: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  p: 0,
  boxShadow:
    "0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)",
  borderRadius: "12px",
  maxHeight: "min(42rem, calc(100vh - 2rem))",
  overflow: "hidden",
};

export default function CampaignAudienceStudentsModal({
  isOpen,
  onClose,
  title,
  subtitle,
  students = [],
}) {
  const [searchTerm, setSearchTerm] = useState("");

  const sortedStudents = useMemo(
    () => [...students].sort((a, b) => a.localeCompare(b)),
    [students],
  );

  const filteredStudents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return sortedStudents;
    return sortedStudents.filter((name) => name.toLowerCase().includes(term));
  }, [sortedStudents, searchTerm]);

  if (!isOpen) return null;

  const handleClose = () => {
    setSearchTerm("");
    onClose();
  };

  const showSearch = sortedStudents.length >= 8;

  return (
    <div className="modal-backdrop campaign-audience-students-modal-backdrop">
      <Box sx={modalBoxStyle} className="campaign-audience-students-modal">
        <div className="user-modal-header">
          <div className="user-modal-header-contents">
            <div className="icon-button-container">
              <div className="x-button-container">
                <ModalCloseButton
                  onClick={handleClose}
                  className="user-x-button"
                />
              </div>
            </div>
            <div className="user-modal-header-title">
              <Title
                title={title || "Students"}
                subtitle={
                  subtitle ||
                  `${students.length} student${students.length === 1 ? "" : "s"}`
                }
                titleSize="1.125rem"
                subtitleSize="0.875rem"
                gap="0.25rem"
                lineHeight="1.75rem"
              />
            </div>
          </div>
        </div>

        <div className="campaign-audience-students-modal-body">
          {showSearch ? (
            <SearchInput
              containerWidth="100%"
              text="Search students..."
              value={searchTerm}
              onChangeText={(e) => setSearchTerm(e.target.value)}
            />
          ) : null}

          <div className="campaign-audience-students-modal-list-wrap">
            <div className="user-modal-text">Students</div>
            <ol className="campaign-audience-students-modal-list">
              {filteredStudents.length === 0 ? (
                <li className="campaign-audience-students-modal-empty">
                  {searchTerm.trim()
                    ? "No students match your search."
                    : "No students listed."}
                </li>
              ) : (
                filteredStudents.map((name, index) => (
                  <li
                    key={`${name}-${index}`}
                    className="campaign-audience-students-modal-item"
                  >
                    <Text
                      size="0.875rem"
                      color="#101828"
                      weight="500"
                      text={name}
                    />
                  </li>
                ))
              )}
            </ol>
          </div>
        </div>

        <div className="campaign-audience-students-modal-actions">
          <SecondaryButton
            width="5rem"
            label="Close"
            imageIcon={false}
            onClickFunction={handleClose}
          />
        </div>
      </Box>
    </div>
  );
}

CampaignAudienceStudentsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  students: PropTypes.arrayOf(PropTypes.string),
};
